
import WDElement from '../wd-element';
import WDElementCollapsible from '../wd-element-collapsible';
import WDButton from '../button/wd-button';
import WDDropdownForm from '../dropdown/wd-dropdown-form';
import WDUtil from '../../core/wd-util';

export class WDListItem extends WDElement
{
	#prefix = null;
	#suffix = null;
	#drawer = null;
	#list = null;
	#checkbox = null;
	#model = {};
	#content = null;
	#icon = null;
	#disclosure = null;
	#drawerToggle = null;
	#isConnected = false;
	#config = {
		columns: [],
		icon: false,
		checkbox: false,
		buttons: [],
		disclosure: false,
		focusable: false,
		sortable: false,
		valueField: 'value',
		drawerHandler: null,
	};

	constructor(cfg)
	{
		super();
		this.classList.add('wd-list-item');
		if (cfg) WDUtil.merge(this.#config,cfg);

		return this;
	};

	connectedCallback()
	{
		//prevents us from running twice in cases of drag-and-drop
		if (this.#isConnected == true) return;
		this.#isConnected = true;

		super.connectedCallback();
		
		//shortcut
		const cfg = this.#config;

		if (cfg?.contentHandler && !(this instanceof WDListHeader))
		{
			cfg.contentHandler(this);
		}
		else
		{
			this.buildContent();
		}
		
	}

	
	buildContent()
	{
		const cfg = this.#config;

		//add icons and checkboxes
		if (cfg.checkbox) this.checkbox.value = this.value;

		if (cfg.sortable) this.icon = 'fa-solid fa-sort';
		else if (cfg.icon) this.icon = cfg.icon;

		if (cfg.buttons.length > 0) this.buttons = cfg.buttons;

		//disclosure indicator		
		if (cfg.disclosure) this.disclosure = true;

		this.addColumns();

		this.addEventHandlers();
		if (cfg?.drawerHandler) cfg?.drawerHandler(this);
		if (cfg?.clickHandler) this.on('item:selected', () => cfg.clickHandler(this));

	}

	addEventHandlers()
	{
		// Add click handler
		this.content.addEventListener('click', (evt) => this.selected = true, { signal: this.abortSignal });

		if (this.#config?.disclosure == true)
		{
			this.disclosure.addEventListener('click', (evt) => this.selected = true, { signal: this.abortSignal });
		}

		// Add keyboard navigation if focusable
		if (this.focusable)
		{
			this.tabIndex = -1;
			this.on('keydown', (e) =>
			{
				if (e.key === 'Enter' || e.key === ' ')
				{
					e.preventDefault();
					this.trigger('item:selected', this);
				}
			});
		}

	};

	getColumnValue(obj, col)
	{
		if (col?.template)
		{
			return String(col.value).interpolate(obj);
		}
		else 
		{
			const path = col.value;
			return path.split('.').reduce((acc, part) => acc && acc[part], obj);
		}
	};

	addColumns()
	{
		//add each configured column to the list item
		this.#config.columns.forEach(col =>
		{
			let cell = this.add();

			//given a custom handler to populate the cell
			if (col?.handler)
			{
				col.handler(cell,this.model,this);
			}
			//extract and format the value from the model
			else
			{
				let val = this.getColumnValue(this.model, col) || '';

				//passed a format handler?  use it
				if (col?.format) val = col.format(val, this.model);
			
				// cell is a wdc() node, must use while loop to trigger disconnectedCallback
				while (cell.firstChild) {
					cell.firstChild.remove();
				}
				cell.append(val);
			}

			//use the value key as the class name
			if (col?.value !== undefined) 
			{
				cell.dataset.column = col.value;
				cell.classList.add(col.value);
			}

			//show a responsive/mobile label
			//options - show when responsive, show different label when responsive, show original label when responsive?
			if (col?.compactHide === true) cell.classList.add('compact-hide');
			else if (col?.compactLabel !== undefined)
			{
				//"responsive" can have the label we want to use, or we just use the original label
				const useLabel = (typeof (col.responsive) == 'string') ? col.compactLabel : col.label;
				cell.classList.add('compact-label');

				const ml = wdc('<label/>');
				ml.append(useLabel);
				cell.prepend(ml);
			}
		});

	};

	get list()
	{
		if (!this.#list) this.#list = this.closest('.wd-list');
		return this.#list;
	};

	get content()
	{
		if (!this.#content) this.content = wdc('<div/>');
		return this.#content;
	};

	get config() 
	{
		return this.#config;
	}

	get sortColumns()
	{
		return this.#config.columns.filter(c => (c.sort == true));
	};

	/**
	 * this allows us to use another element as the content wrapper, like
	 * in the case where we embed a form within a list row in wd-list-form-item
	 */
	set content(elem)
	{
		this.#content = elem;
		this.#content.classList.add('wd-list-item-content');
		this.append(this.#content);
	};

	get prefix()
	{
		if (!this.#prefix)
		{
			this.#prefix = wdc('<div class="wd-list-item-prefix"/>');
			this.content.before(this.#prefix);
		}

		return this.#prefix;
	};

	get suffix()
	{
		if (!this.#suffix)
		{
			this.#suffix = wdc('<div class="wd-list-item-suffix"/>');
			this.content.after(this.#suffix);
		}

		return this.#suffix;
	};

	//needs to be set before the drawer is created
	set model(val) { 
		this.#model = val; 
		if ( typeof(val[this.#config.valueField]) != 'undefined') 
		{
			this.dataset.value = val[this.#config.valueField];
		}
	};
	
	get model() { return this.#model; };

	//needs to be set before the drawer is created
	set drawerToggle(val) { this.#drawerToggle = val; };
	get drawerToggle() { return this.#drawerToggle; };

	get drawer()
	{
		if (!this.#drawer)
		{
			this.#drawer = new WDElementCollapsible({
				collapsed: true,
			});

			if (this.#suffix) this.#suffix.after(this.#drawer);
			else this.content.after(this.#drawer);

			this.#drawer.on('element:open', () => this.classList.add('drawer-open'));
			this.#drawer.on('element:close', () => this.classList.remove('drawer-open'));

			/** what toggles this?  an icon or the row? */
			if (this.drawerToggle) 
			{
				this.drawerToggle.addEventListener('click', () => this.drawer.toggle(), { signal: this.abortSignal } );
			}
			else
			{
				if (this.#prefix)
				{
					this.#prefix.addEventListener('click', evt => {
						evt.preventDefault();
						this.drawer.toggle();
					}, { signal: this.abortSignal });
				}

				this.content.addEventListener('click', evt => {
					evt.preventDefault();
					this.drawer.toggle();
				}, { signal: this.abortSignal });
			}

			this.drawer.closest('.wd-element-collapsible-wrapper').classList.add('wd-list-item-drawer');
		}

		return this.#drawer;
	}

	add(content)
	{
		const cell = wdc('<div class="wd-list-item-cell"/>');

		if (content !== undefined && content !== null)
		{
			cell.append(content);
		}
		
		this.content.append(cell);

		return cell;
	};

	get value()
	{
		//a little hack.  In a lot of cases valueField wasn't set when calling the list,
		//and there's no 'value' property in the data.  In that case, override valueField
		//with 'id' (because it's used a lot of the time)
		if (this.config.valueField == 'value' && this.model?.value === undefined && this.model?.id !== undefined)
		{
			this.config.valueField = 'id';
			this.list.config.valueField = 'id';	//stops us from having to do this again for us
												//or for other elements
		}

		return this.model[this.config.valueField] || undefined;
	};

	get checkbox()
	{
		if (!this.#checkbox)
		{
			this.#checkbox = wdc('<input type="checkbox" name="list_item[]"/>');
			this.#checkbox.setAttribute('value', this.value);

			this.#checkbox.addEventListener('change', (evt) =>
			{
				const evtName = (this.#checkbox.checked == true) ? 'item:checked' : 'item:unchecked';
				this.trigger(evtName, this);
			}, { signal: this.abortSignal });

			const cell = wdc('<div class="wd-list-item-checkbox"/>');
			cell.append(this.#checkbox);
			this.prefix.prepend(cell);

			this.on('item:checked item:unchecked', () => this.handleCheckedEvent());
		}

		return this.#checkbox;
	};

	set checkbox(val)
	{
		if (val === true) this.checkbox;
		else if (val === false && this.#checkbox) 
		{
			this.#checkbox.remove();
			this.#checkbox = null;
		}
	};

	get checked() {
		return (this.#checkbox) ? this.#checkbox.checked : false; 
	};

	set checked(val)
	{
		if (this.#checkbox.disabled == true) return this;

		this.#checkbox.checked = val;

		if (!this.list.suspendEvents)
		{
			const evtName = (val === true) ? 'item:checked' : 'item:unchecked';
			this.trigger(evtName, this);
		}
		
	};

	handleCheckedEvent()
	{
		if (this.#checkbox.checked == true) this.classList.add('is-checked');
		else this.classList.remove('is-checked');
	};

	set buttons(val)
	{
		val.forEach( btnCfg => this.addButton(btnCfg) );
	};

	//adds a button onto the row
	addButton(cfg)
	{
		const btn = new WDButton(cfg);
		this.suffix.append(btn);
		return btn;
	};

	/**
		icon, thumbnail, buttons, disclosure
		- should we just have a prefix/suffix?
		- may end up being more code having to write the the icon or disclosure
		- what about thumbnails?  are they used enough to keep?  (or call them image?)
		*/

	get icon()
	{
		if (!this.#icon)
		{
			this.#icon = wdc('<span/>');
			const cont = wdc('<div class="wd-list-item-icon"/>').append(this.#icon);
			this.prefix.append(this.#icon);
		}
		return this.#icon;
	};

	set icon(iconParam)
	{
		//given a string template - use data property for the icon
		if (iconParam.includes('${'))
		{
			//setup a fallback and then try to interpolate if we have data
			let iconstr = 'fa-solid fa-icons';
			if (this.model) iconstr = iconParam.interpolate(this.model);

			this.icon.setAttribute('class', iconstr);
		}
		else
		{
			const icons = Array.isArray(iconParam) ? iconParam : iconParam.split(' ');
			this.icon.setAttribute('class', '');
			this.icon.classList.add(...icons);
		}
	};

	get disclosure()
	{
		if (!this.#disclosure)
		{
			//allow icon override
			this.#disclosure = new WDButton({ theme: 'clear', icon: 'fa-solid fa-chevron-right' });

			const wrapper = wdc('<div class="wd-list-item-disclosure"/>');
			wrapper.append(this.#disclosure);

			this.suffix.append(wrapper);
		}
		return this.#disclosure;
	};

	set disclosure(icon)
	{
		this.disclosure.icon = typeof (icon) == 'string' ? icon : 'fa-solid fa-chevron-right';
	};

	get selected() 
	{ 
		return this.classList.contains('is-selected'); 
	};

	set selected(val)
	{
		if (val === true)
		{
			this.list.deselectAll(this);

			this.classList.add('is-selected');
			this.scrollTo();
		}
		else
		{
			this.classList.remove('is-selected');
		}

		if (!this.list.suspendEvents)
		{
			const evtName = (val === true) ? 'item:selected' : 'item:unselected';
			this.trigger(evtName, this);
		}
	};

	get index() { 
		return this?.parentElement? Array.prototype.indexOf.call(this.parentElement.children, this) : -1;
	}

	get nextItem()
	{
		return this.nextElementSibling;
	};

	get prevItem()
	{
		return this.previousElementSibling;
	};

	scrollTo()
	{
		if (!this.list) return; 

		const wrapper = this.list.wrapper;
		const tolerance = 5; // Only scroll if more than 10px is out of view

		// Get the actual position of the item relative to the wrapper (scrollable container)
		const itemRect = this.getBoundingClientRect();
		const wrapperRect = wrapper.getBoundingClientRect();

		// Calculate positions relative to the wrapper's scroll position
		const itemTop = itemRect.top - wrapperRect.top + wrapper.scrollTop;
		const itemBottom = itemTop + itemRect.height;

		// Define the visible area boundaries
		const visibleTop = wrapper.scrollTop;
		const visibleBottom = wrapper.scrollTop + wrapperRect.height;

		// Calculate how much of the item is out of view
		const pixelsAbove = Math.max(0, visibleTop - itemTop);
		const pixelsBelow = Math.max(0, itemBottom - visibleBottom);

		// Only scroll if more than the tolerance is hidden
		if (pixelsAbove <= tolerance && pixelsBelow <= tolerance)
		{
			return; // Item is sufficiently visible, no need to scroll
		}

		// Calculate optimal scroll position
		let targetScrollTop;

		if (pixelsAbove > tolerance)
		{
			// Item is clipped at the top - scroll up to show it with some padding
			targetScrollTop = itemTop - tolerance;
		}
		else if (pixelsBelow > tolerance)
		{
			// Item is clipped at the bottom - scroll down to show it with some padding
			targetScrollTop = itemBottom - wrapperRect.height + tolerance;
		}

		// Ensure we don't scroll beyond bounds
		targetScrollTop = Math.max(0, Math.min(targetScrollTop, wrapper.scrollHeight - wrapperRect.height));
		const scrollBehavior = this.list.suspendAnimations === true ? 'auto' : 'smooth';
	
		// Use smooth scrolling
		if (wrapper.scrollTo)
		{
			wrapper.scrollTo({
				top: targetScrollTop,
				behavior: scrollBehavior,
			});
		}
		else
		{
			const originalScrollBehavior = wrapper.style.scrollBehavior;

			wrapper.style.scrollBehavior = scrollBehavior;	
			wrapper.scrollTop = targetScrollTop;
			
			// Restore original scroll behavior
			wrapper.style.scrollBehavior = originalScrollBehavior;
		}
	};
	
	cleanupCallback()
	{
		// Call parent cleanup FIRST
		super.cleanupCallback();
		
		// Clear DOM element references
		this.#drawer = null;
		this.#disclosure = null;
		this.#prefix = null;
		this.#suffix = null;
		this.#content = null;
		this.#checkbox = null;
		this.#icon = null;
		this.#drawerToggle = null;

		// Clear list reference and model (model handles its own cleanup)
		this.#list = null;
		this.#model = null;
		this.#config = null;
	}

};

customElements.define('wd-list-item', WDListItem);

export class WDListHeader extends WDListItem
{
	
	#mobileSort = null;
	#checkbox = null;

	constructor(cfg)
	{
		super(cfg);
		this.classList.add('wd-list-header');
	};

	connectedCallback()
	{
		super.connectedCallback();

		this.list.addEventListener('sort:updated',() => {
			this.updateSort();
		}, { signal: this.abortSignal });

		const sortCols = this.config.columns.filter(col => col?.sortable == true);
		if (sortCols.length > 0) {
			this.addMobileSort();
			this.classList.add('has-sort');
		}

		//keep the select-all checkbox in sync with the rows
		if (this.config.selectAll)
		{
			['item:checked', 'item:unchecked', 'items:updated'].forEach(evtName =>
			{
				this.list.addEventListener(evtName, () => this.updateSelectAllState(), { signal: this.abortSignal });
			});
		}
	}

	buildContent()
	{
		const cfg = this.config;

		//add icons and checkboxes
		if (cfg.selectAll) this.selectAll = true;
		else if (cfg.checkbox) this.checkbox;

		if (cfg.sortable) this.icon = 'fa-solid fa-sort';
		else if (cfg.icon) this.icon = cfg.icon;

		if (cfg.buttons.length > 0) this.buttons = cfg.buttons;

		//disclosure indicator		
		if (cfg.disclosure) this.disclosure = true;

		this.addColumns();
	}

	addColumns()
	{
		//add each configured column to the list item
		this.config.columns.forEach(col =>
		{
			let cell = this.add(col.label);
			if (col?.sortable) this.makeColumnSortable(cell,col);

			//use the value key as the class name
			if (col?.value !== undefined) 
			{
				cell.dataset.column = col.value;
				cell.classList.add(col.value);
			}
		});

	};

	getColumn(colName)
	{
		return this.querySelector(`[data-column="${colName}"`);
	}

	makeColumnSortable(elem,col)
	{
		elem.classList.add('is-sortable');

		//add a button and replace the content
		const sortButton = new WDButton({
			label: col.label || col.value,
			icon: this.getSortIcon(col.value)
		});

		sortButton.classList.add('wd-list-sort-button');
		sortButton.on('click', () => this.requestSort(col.value));

		// elem is a wdc() cell node, must use while loop to trigger disconnectedCallback
		while (elem.firstChild) {
			elem.firstChild.remove();
		}
		elem.append(sortButton);
	}

	getSortIcon(field)
	{
		if (field !== this.list.sortField) return null;
		return this.list.sortDir === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down';
	}

	addMobileSort()
	{
		this.#mobileSort = new WDDropdownForm({
			label: 'Sort',
			value: this.list.sortField,
			options: this.sortColumns.map(col => ({
				label: col.label || col.value,
				value: col.value
			}))
		});

		this.#mobileSort.classList.add('wd-list-mobile-sort','wd-list-item-cell');
		this.#mobileSort.on('change', (evt) => {
			this.requestSort(evt.detail.value);
		});

		this.prepend(this.#mobileSort);
	}

	// Request a sort - triggers event for parent to handle
	requestSort(field)
	{
		// Determine new direction
		let newDir = 'asc';
		if (field === this.list.sortField && this.list.sortDir === 'asc') {
			newDir = 'desc';
		}

		this.trigger('sort:requested', {
			field: field,
			direction: newDir
		});
	}

	// Update the current sort state (called by parent)
	updateSort(field, direction)
	{
		const sortCols = this.querySelectorAll('.is-sortable');
		sortCols.forEach( col => {
			col.children[0].icon = this.getSortIcon(col.dataset.column);
		});

		// Update mobile sort selection
		if (this.#mobileSort)
		{
			this.#mobileSort.value = this.list.sortField;
		}
	};

	set checkbox(val)
	{
		if (val === true) this.checkbox;
		else if (val === false && this.#checkbox) 
		{
			this.#checkbox.remove();
			this.#checkbox = null;
		}
	};

	/**
	 * select-all mode shows a real checkbox in the header that checks/unchecks
	 * every enabled row and reflects their state (checked/indeterminate)
	 */
	get selectAll() { return this.classList.contains('has-select-all'); };

	set selectAll(val)
	{
		if (val === true)
		{
			this.config.selectAll = true;
			this.classList.add('has-select-all');
			this.checkbox;
		}
		else
		{
			this.config.selectAll = false;
			this.classList.remove('has-select-all', 'is-checked', 'is-indeterminate');
		}
	};

	get checkbox()
	{
		if (!this.#checkbox)
		{
			const cell = wdc('<div class="wd-list-item-checkbox"/>');

			if (this.config.selectAll)
			{
				this.#checkbox = wdc('<input type="checkbox" class="wd-list-select-all" title="Select All"/>');
				this.#checkbox.addEventListener('change', () => this.checkAll(this.#checkbox.checked), { signal: this.abortSignal });
				cell.append(this.#checkbox);
			}
			else
			{
				//legacy check icon.  hidden by default, panes may expose it themselves
				this.#checkbox = wdc('<span class="fa-solid fa-check"/>');
				cell.append(this.#checkbox);
				cell.addEventListener("click", () => this.checkAll(), { signal: this.abortSignal });
			}

			this.prefix.prepend(cell);
		}

		return this.#checkbox;
	};

	get enabledItems()
	{
		return this.list.itemArray.filter(x => x.checkbox.disabled === false);
	};

	/**
	 * @param {boolean} mode - check (true) or uncheck (false).  Toggles when not passed
	 */
	checkAll(mode)
	{
		const enabledItems = this.enabledItems;
		if (enabledItems.length === 0) return;

		const checkMode = (typeof mode === 'boolean') ? mode : (enabledItems[0].checked === true ? false : true);

		this.list.suspendEvents = true;

		enabledItems.forEach( item => {
			item.checked = checkMode;
		});

		this.list.suspendEvents = false;

		this.updateSelectAllState();

		this.list.trigger('list:checkall', {
			checked: checkMode,
			items: enabledItems
		});

		this.list.trigger('change');

	};

	updateSelectAllState()
	{
		if (!this.config?.selectAll || !this.#checkbox) return;

		const enabledItems = this.enabledItems;
		const checkedCount = enabledItems.filter(x => x.checked === true).length;

		this.#checkbox.checked = enabledItems.length > 0 && checkedCount === enabledItems.length;
		this.#checkbox.indeterminate = checkedCount > 0 && checkedCount < enabledItems.length;

		this.classList.toggle('is-checked', this.#checkbox.checked);
		this.classList.toggle('is-indeterminate', this.#checkbox.indeterminate);
	};

	cleanupCallback()
	{
		// Call parent cleanup FIRST
		super.cleanupCallback();
		
		// Clean up mobile sort dropdown
		if (this.#mobileSort) {
			this.#mobileSort = null;
		}

		// Clear checkbox reference
		this.#checkbox = null;
	}

};

customElements.define('wd-list-header', WDListHeader);

export class WDListFooter extends WDListItem
{
	#checkbox = null;

	constructor()
	{
		super();
		this.classList.add('wd-list-footer');
	};
};

customElements.define('wd-list-footer', WDListFooter);
